import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

// Kind 31733 for app submissions (addressable events)
const APP_SUBMISSION_KIND = 31733;

// The moderator npub and hex
const MODERATOR_NPUB = 'npub1jvnpg4c6ljadf5t6ry0w9q0rnm4mksde87kglkrc993z46c39axsgq89sc';
const MODERATOR_HEX = (() => {
  try {
    const decoded = nip19.decode(MODERATOR_NPUB);
    return decoded.type === 'npub' ? decoded.data : '';
  } catch {
    return '';
  }
})();

// Soapbox team follow pack naddr (same as used on About page)
const SOAPBOX_TEAM_NADDR = 'naddr1qvzqqqyckypzpyexz3t34l966ngh5xg7u2q788hthdqmj0av3lv8s2tz9t43zt6dqqxxkdrsx4mnqm3jxfeh2ess5pyrw';

export interface AppSubmission extends NostrEvent {
  appName: string;
  websiteUrl: string;
  repositoryUrl: string;
  description: string;
  appIconUrl: string;
  screenshotUrl: string;
  coverImageUrl?: string;
  appTags: string[];
  authorNpub: string;
  isFeatured: boolean;
  isApproved: boolean;
  isHidden: boolean;
  isHomepage: boolean;
}

interface UseAppSubmissionsOptions {
  enabled?: boolean;
}

export function useAppSubmissions(options: UseAppSubmissionsOptions = {}) {
  const { nostr } = useNostr();
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['nostr', 'app-submissions'],
    enabled,
    queryFn: async (c): Promise<AppSubmission[]> => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Parallelize all Nostr queries for faster loading
      const [teamEvents, submissionEvents, moderationLists, reportEvents] = await Promise.all([
        // Get Soapbox team follow pack for auto-approval
        (async () => {
          try {
            const decoded = nip19.decode(SOAPBOX_TEAM_NADDR);
            if (decoded.type === 'naddr') {
              const addrData = decoded.data;
              return await nostr.query([{
                kinds: [addrData.kind],
                authors: [addrData.pubkey],
                '#d': [addrData.identifier],
                limit: 1
              }], { signal });
            }
          } catch (error) {
            console.warn('Failed to fetch Soapbox team follow pack:', error);
          }
          return [];
        })(),

        // Get all app submissions (kind 31733)
        nostr.query([{
          kinds: [APP_SUBMISSION_KIND],
          '#t': ['soapbox-app-submission'],
          limit: 500 // Reduced from 1000 for better performance
        }], { signal }),

        // Get all moderation lists in one query (featured, approved, homepage)
        nostr.query([{
          kinds: [30267],
          authors: [MODERATOR_HEX],
          '#d': ['soapbox-featured-apps', 'soapbox-approved-apps', 'soapbox-homepage-apps'],
          limit: 30
        }], { signal }),

        // Get reporting events for hidden apps (NIP-56 kind 1984)
        nostr.query([{
          kinds: [1984],
          authors: [MODERATOR_HEX],
          limit: 500 // Reduced from 1000 for better performance
        }], { signal })
      ]);

      // Extract team pubkeys
      const teamPubkeys = new Set<string>();
      if (teamEvents.length > 0) {
        teamEvents[0].tags
          .filter(([name]) => name === 'p')
          .forEach(([, pubkey]) => {
            if (pubkey) teamPubkeys.add(pubkey);
          });
      }

      // Separate moderation lists by d-tag
      const featuredLists = moderationLists.filter(e =>
        e.tags.some(([name, value]) => name === 'd' && value === 'soapbox-featured-apps')
      );
      const approvedLists = moderationLists.filter(e =>
        e.tags.some(([name, value]) => name === 'd' && value === 'soapbox-approved-apps')
      );
      const homepageLists = moderationLists.filter(e =>
        e.tags.some(([name, value]) => name === 'd' && value === 'soapbox-homepage-apps')
      );

      // Process submissions and apply moderation (optimized)
      const submissions: AppSubmission[] = [];
      const submissionMap = new Map<string, NostrEvent>();

      // Group regular submissions by d-tag (identifier) to get latest version
      // Use reduce for better performance than forEach
      submissionEvents.reduce((map, event) => {
        const dTag = event.tags.find(tag => tag[0] === 'd')?.[1];
        if (!dTag) return map;

        const existing = map.get(dTag);
        if (!existing || event.created_at > existing.created_at) {
          map.set(dTag, event);
        }
        return map;
      }, submissionMap);

      // Get the latest lists (optimized)
      const latestFeaturedList = featuredLists.length > 0
        ? featuredLists.reduce((latest, current) =>
            current.created_at > latest.created_at ? current : latest
          )
        : null;

      const latestApprovedList = approvedLists.length > 0
        ? approvedLists.reduce((latest, current) =>
            current.created_at > latest.created_at ? current : latest
          )
        : null;

      const latestHomepageList = homepageLists.length > 0
        ? homepageLists.reduce((latest, current) =>
            current.created_at > latest.created_at ? current : latest
          )
        : null;

      // Extract app coordinates from lists (optimized with single pass)
      const featuredAppCoords = new Set<string>(
        latestFeaturedList?.tags
          .filter(tag => tag[0] === 'a')
          .map(tag => tag[1]) || []
      );

      const approvedAppCoords = new Set<string>(
        latestApprovedList?.tags
          .filter(tag => tag[0] === 'a')
          .map(tag => tag[1]) || []
      );

      const homepageAppCoords = new Set<string>(
        latestHomepageList?.tags
          .filter(tag => tag[0] === 'a')
          .map(tag => tag[1]) || []
      );

      // Get hidden app coordinates from reports (optimized)
      const appReports = new Map<string, { hidden: number; unhidden: number }>();

      // Process reports in a single pass
      reportEvents.forEach(report => {
        const aTag = report.tags.find(tag => tag[0] === 'a');
        if (!aTag?.[1]) return;

        const appCoord = aTag[1];
        const reportType = aTag[2];
        const moderationLabel = report.tags.find(tag =>
          tag[0] === 'l' && tag[2] === 'soapbox.moderation'
        )?.[1];

        if (!moderationLabel) return;

        const reportData = appReports.get(appCoord) || { hidden: 0, unhidden: 0 };

        if (moderationLabel === 'hidden' && (reportType === 'spam' || reportType === 'other')) {
          reportData.hidden = Math.max(reportData.hidden, report.created_at);
        } else if (moderationLabel === 'unhidden' && reportType === 'other') {
          reportData.unhidden = Math.max(reportData.unhidden, report.created_at);
        }

        appReports.set(appCoord, reportData);
      });

      // Determine which apps are currently hidden (latest action wins)
      const hiddenAppCoords = new Set<string>();
      appReports.forEach((reports, appCoord) => {
        if (reports.hidden > reports.unhidden) {
          hiddenAppCoords.add(appCoord);
        }
      });

      // Convert to AppSubmission objects
      for (const [dTag, event] of submissionMap) {
        try {
          const appName = event.tags.find(tag => tag[0] === 'title')?.[1] || '';
          const websiteUrl = event.tags.find(tag => tag[0] === 'website')?.[1] || '';
          const repositoryUrl = event.tags.find(tag => tag[0] === 'repository')?.[1] || '';
          const appIconUrl = event.tags.find(tag => tag[0] === 'icon')?.[1] || '';
          const screenshotUrl = event.tags.find(tag => tag[0] === 'screenshot')?.[1] || '';
          const coverImageUrl = event.tags.find(tag => tag[0] === 'image')?.[1];
          const authorNpub = event.tags.find(tag => tag[0] === 'author')?.[1] || event.pubkey;
          const appTags = event.tags.filter(tag => tag[0] === 'app-tag').map(tag => tag[1]);

          // Check if app is featured, approved, homepage, or hidden
          const appCoordinate = `${APP_SUBMISSION_KIND}:${event.pubkey}:${dTag}`;
          const isFeatured = featuredAppCoords.has(appCoordinate);
          const isHomepage = homepageAppCoords.has(appCoordinate);
          const isSubmittedByModerator = event.pubkey === MODERATOR_HEX;
          const isSubmittedByTeamMember = teamPubkeys.has(event.pubkey);
          const isApproved = approvedAppCoords.has(appCoordinate) || isFeatured || isSubmittedByModerator || isSubmittedByTeamMember;
          const isHidden = hiddenAppCoords.has(appCoordinate);

          submissions.push({
            ...event,
            appName,
            websiteUrl,
            repositoryUrl,
            description: event.content,
            appIconUrl,
            screenshotUrl,
            coverImageUrl,
            appTags: appTags,
            authorNpub,
            isFeatured,
            isApproved,
            isHidden,
            isHomepage
          });
        } catch (error) {
          console.warn('Failed to parse app submission:', error);
        }
      }

      return submissions;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - apps don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    retry: 1, // Only retry once if it fails
  });
}

export function useUserAppSubmissions(userPubkey?: string) {
  const { data: allSubmissions, ...rest } = useAppSubmissions();

  const userSubmissions = allSubmissions?.filter(app => app.pubkey === userPubkey) || [];

  return {
    data: userSubmissions,
    ...rest
  };
}